import { FC } from 'react';
import { Tooltip } from '@mui/material';
import StatusTag from './StatusTag';

interface BeraterInaktivBadgeProps {
  /** users.is_active des Case-Owners. undefined = Feld nicht geladen -> kein Hinweis. */
  isActive?: boolean | null;
  /** Fuer den Tooltip-Text; optional. */
  name?: string | null;
  /**
   * Kompakte Variante fuer Listen und Auswahlfelder (Alex 10.09.2026).
   *
   * Sie zeigt nur „inaktiv" statt „Berater inaktiv" und traegt einen
   * kontextfreien Tooltip. Gebraucht wird sie im Berater-Filter der
   * Meetings-Liste: dort stehen bis zu 200 Namen untereinander, und der
   * volle Tag macht jede zweite Zeile zur Warnung.
   *
   * 🔥 WICHTIGER ALS DIE BREITE IST DER TEXT: der Standard-Tooltip erklaert
   * die REAKTIVIERUNGS-Folge am Fall („der Fall wird nicht mehr reaktiviert").
   * In einem Berater-Filter gibt es keinen Fall — die Aussage waere dort
   * schlicht falsch. Deshalb eine Variante an derselben Komponente und
   * keine zweite daneben (Decision #116: kanonische Komponente erweitern).
   */
  kompakt?: boolean;
}

/**
 * Kennzeichnung "Berater inaktiv" am Case-Owner (Decision #136 Punkt 2,
 * Alex 29.07.2026).
 *
 * Warum ueberhaupt: Ein deaktivierter Owner bedeutet seit derselben
 * Entscheidung, dass der Fall NICHT mehr reaktiviert wird — eine Wiederbuchung
 * laeuft als neuer Vorgang ueber die normale Verteilung. Diese Folge war bisher
 * unsichtbar; man sah einen Fall mit Berater und ahnte nicht, dass hinter dem
 * Namen niemand mehr steht.
 *
 * Bewusst rein anzeigend: keine Buchungs-, Verteilungs- oder WRR-Logik haengt
 * daran. Und bewusst ueber StatusTag, damit hier kein Hex-Wert steht
 * (CLAUDE.md §5).
 *
 * `isActive === undefined` heisst "Feld nicht mitgeladen" und wird bewusst wie
 * aktiv behandelt: lieber kein Hinweis als ein falscher an jedem Fall, dessen
 * Liste den Owner-Datensatz gar nicht mitliefert.
 */
const BeraterInaktivBadge: FC<BeraterInaktivBadgeProps> = ({ isActive, name, kompakt = false }) => {
  if (isActive !== false) return null;

  const titel = kompakt
    ? `${name ? `${name} ist` : 'Dieser Berater ist'} deaktiviert — die Termine bleiben sichtbar, neue Zuteilungen bekommt er nicht.`
    : name
      ? `${name} ist deaktiviert. Der Fall wird nicht mehr reaktiviert — eine Wiederbuchung läuft als neuer Vorgang über die normale Verteilung.`
      : 'Der zugewiesene Berater ist deaktiviert. Der Fall wird nicht mehr reaktiviert — eine Wiederbuchung läuft als neuer Vorgang über die normale Verteilung.';

  return (
    <Tooltip title={titel}>
      {/* span: Tooltip braucht ein Element, das Ref und Events annimmt. */}
      <span>
        <StatusTag label={kompakt ? 'inaktiv' : 'Berater inaktiv'} variant="red" />
      </span>
    </Tooltip>
  );
};

export default BeraterInaktivBadge;
